<script>
import CustomButton from 'shared/components/Button.vue';
import TextArea from 'shared/components/TextArea.vue';
import Spinner from 'shared/components/Spinner.vue';

export default {
  name: 'Feedback',
  components: {
    CustomButton,
    TextArea,
    Spinner,
  },
  props: {
    isUpdating: {
      type: Boolean,
      default: false,
    },
    selectedRating: {
      type: Number,
      default: null,
    },
    channelName: {
      type: String,
      default: null,
    },
  },
  emits: ['sendFeedback'],
  data() {
    return {
      feedback: '',
      easeOfInteraction: null,
      issueResolution: null,
    };
  },
  computed: {
    isButtonDisabled() {
      return !(
        this.easeOfInteraction &&
        this.issueResolution !== null &&
        this.selectedRating
      );
    },
  },
  methods: {
    onClick() {
      const feedbackData = {
        feedback: this.feedback,
        easeOfInteraction: this.easeOfInteraction,
        issueResolution: this.issueResolution,
      };
      this.$emit('sendFeedback', feedbackData);
    },
    selectEaseOfInteraction(value) {
      this.easeOfInteraction = value;
    },
    selectIssueResolution(value) {
      this.issueResolution = value;
    },
  },
};
</script>

<template>
  <div class="mt-6">
    <!-- Channel Ease of Interaction -->
    <div class="mt-6 mb-6">
      <p class="mb-4 text-base font-medium text-black-800">
        {{ $t('SURVEY.EASE_OF_INTERACTION.QUESTION', { channelName }) }}
      </p>
      <div class="space-y-2">
        <label
          :class="{
            active: easeOfInteraction === 'easy',
          }"
          class="block feedback-form__label p-[10px] rounded cursor-pointer"
        >
          <input
            type="radio"
            name="ease-of-interaction"
            value="easy"
            class="mr-2 focus:ring-green-500 hidden"
            @change="selectEaseOfInteraction('easy')"
          />
          {{ $t('SURVEY.EASE_OF_INTERACTION.EASY') }}
        </label>

        <label
          :class="{
            active: easeOfInteraction === 'neutral',
          }"
          class="block feedback-form__label p-[10px] rounded cursor-pointer"
        >
          <input
            type="radio"
            name="ease-of-interaction"
            value="neutral"
            class="mr-2 focus:ring-yellow-500 hidden"
            @change="selectEaseOfInteraction('neutral')"
          />
          {{ $t('SURVEY.EASE_OF_INTERACTION.NEUTRAL') }}
        </label>

        <label
          :class="{
            active: easeOfInteraction === 'difficult',
          }"
          class="block feedback-form__label p-[10px] rounded cursor-pointer"
        >
          <input
            type="radio"
            name="ease-of-interaction"
            value="difficult"
            class="mr-2 focus:ring-red-500 hidden"
            @change="selectEaseOfInteraction('difficult')"
          />
          {{ $t('SURVEY.EASE_OF_INTERACTION.DIFFICULT') }}
        </label>
      </div>
    </div>

    <!-- Issue Resolution -->
    <div class="mt-4">
      <p class="font-medium text-gray-700 mb-2">
        {{ $t('SURVEY.ISSUE_RESOLUTION.QUESTION', { channelName }) }}
      </p>
      <div class="space-y-2">
        <label
          :class="{
            active: issueResolution === true,
          }"
          class="block feedback-form__label p-[10px] rounded cursor-pointer"
        >
          <input
            type="radio"
            name="issue-resolution"
            value="true"
            class="mr-2 focus:ring-green-500 hidden"
            @change="selectIssueResolution(true)"
          />
          {{ $t('SURVEY.ISSUE_RESOLUTION.YES') }}
        </label>

        <label
          :class="{
            active: issueResolution === false,
          }"
          class="block feedback-form__label p-[10px] rounded cursor-pointer"
        >
          <input
            type="radio"
            name="issue-resolution"
            value="false"
            class="mr-2 focus:ring-red-500 hidden"
            @change="selectIssueResolution(false)"
          />
          {{ $t('SURVEY.ISSUE_RESOLUTION.NO') }}
        </label>
      </div>
    </div>

    <!-- Feedback Form -->
    <label class="text-base font-medium text-black-800 mt-6 block">
      {{ $t('SURVEY.FEEDBACK.LABEL') }}
    </label>
    <TextArea
      v-model="feedback"
      class="my-5"
      :placeholder="$t('SURVEY.FEEDBACK.PLACEHOLDER')"
    />

    <!-- Submit Button -->
    <div class="flex items-center float-right font-medium">
      <CustomButton :disabled="isButtonDisabled || isUpdating" @click="onClick">
        <Spinner v-if="isUpdating" class="p-0" />
        {{ $t('SURVEY.BUTTON.SUBMIT') }}
      </CustomButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import 'widget/assets/scss/variables.scss';

label.feedback-form__label {
  background-color: #d3d3d3;
  margin: 10px 0;

  &:hover {
    background-color: lighten(#d3d3d3, 10%);
  }

  &.active {
    background-color: $color-error; // Adjust the color for active state
    color: white;
  }
}

.bg-woot-500:disabled {
  background-color: #d3d3d3;
}

</style>
